import React, { useState, useMemo } from 'react';
import {
  Eye,
  Package,
  Truck,
  ExternalLink,
  CheckCircle,
  Circle,
  Loader2,
  ChevronUp,
  ChevronDown,
  ArrowUpDown,
  Clock,
  DollarSign,
  Hash,
  Calendar,
  User,
  MapPin,
  Mail,
  Phone,
  Copy,
  Check,
  Printer,
  FileText,
  PackageCheck,
  ShoppingBag,
  Globe,
  AlertCircle,
  ClipboardList,
  ListChecks,
} from 'lucide-react';
import type { ShopifyOrder } from '../../types/shopify';
import {
  formatCurrency,
  getRelativeTime,
  getFinancialStatusColor,
  getFulfillmentDisplayColor,
  getFulfillmentDisplayLabel,
  isOrderAwaitingFulfillment,
} from '../../types/shopify';

interface OrdersListProps {
  orders: ShopifyOrder[];
  onOrderClick: (order: ShopifyOrder) => void;
  onToggleReady?: (orderId: string, currentReady: boolean) => Promise<void>;
  onFulfillClick?: (order: ShopifyOrder) => void;
  onFinishClick?: (order: ShopifyOrder) => void;
  onReprintClick?: (order: ShopifyOrder) => void;
  onPrintInvoiceClick?: (order: ShopifyOrder) => void;
  onBulkPrintPick?: (selectedOrderIds: string[]) => void;
  onBulkMarkReady?: (selectedOrderIds: string[]) => void;
  onBulkFinish?: (selectedOrderIds: string[]) => void;
  togglingReadyId?: string | null;
  finishingOrderId?: number | null;
  reprintingOrderId?: number | null;
}

type SortField = 'order_number' | 'order_date' | 'total_price' | 'financial_status' | 'fulfillment_status' | 'customer_name';
type SortDirection = 'asc' | 'desc';

const getTrackingUrl = (carrier: string | null, trackingCode: string): string | null => {
  if (!carrier || !trackingCode) return null;

  const carrierUrls: Record<string, string> = {
    'PostNL': `https://postnl.nl/tracktrace/?B=${trackingCode}`,
    'DHL': `https://www.dhl.com/nl-en/home/tracking.html?tracking-id=${trackingCode}`,
    'DPD': `https://tracking.dpd.de/status/en_US/parcel/${trackingCode}`,
    'UPS': `https://www.ups.com/track?loc=en_US&tracknum=${trackingCode}`,
    'FedEx': `https://www.fedex.com/fedextrack/?trknbr=${trackingCode}`,
    'GLS': `https://gls-group.eu/NL/en/parcel-tracking?match=${trackingCode}`,
    'Budbee': `https://tracking.budbee.com/${trackingCode}`,
  };

  return carrierUrls[carrier] || null;
};

const formatFullAddress = (order: ShopifyOrder): string => {
  if (!order.shipping_address) return '';
  const parts = [
    order.shipping_address.city,
    order.shipping_address.province_code,
    order.shipping_address.country,
  ].filter(Boolean);
  return parts.join(', ');
};

const getCustomerInitials = (order: ShopifyOrder): string => {
  if (order.customer_first_name && order.customer_last_name) {
    return `${order.customer_first_name[0]}${order.customer_last_name[0]}`.toUpperCase();
  }
  if (order.customer_name) {
    const parts = order.customer_name.split(' ');
    if (parts.length >= 2) {
      return `${parts[0][0]}${parts[1][0]}`.toUpperCase();
    }
    return order.customer_name.substring(0, 2).toUpperCase();
  }
  return '??';
};

const getOrderAge = (orderDate: string): { days: number; urgent: boolean; warning: boolean } => {
  const now = new Date();
  const created = new Date(orderDate);
  const diffMs = now.getTime() - created.getTime();
  const days = Math.floor(diffMs / (1000 * 60 * 60 * 24));
  return {
    days,
    urgent: days >= 3,
    warning: days >= 2 && days < 3,
  };
};

const OrdersList: React.FC<OrdersListProps> = React.memo(({
  orders,
  onOrderClick,
  onToggleReady,
  onFulfillClick,
  onFinishClick,
  onReprintClick,
  onPrintInvoiceClick,
  onBulkPrintPick,
  onBulkMarkReady,
  onBulkFinish,
  togglingReadyId,
  finishingOrderId,
  reprintingOrderId,
}) => {
  const [sortField, setSortField] = useState<SortField>('order_date');
  const [sortDirection, setSortDirection] = useState<SortDirection>('desc');
  const [copiedId, setCopiedId] = useState<string | null>(null);
  const [selectedOrders, setSelectedOrders] = useState<Set<string>>(new Set());

  const copyToClipboard = (text: string, id: string, e: React.MouseEvent) => {
    e.stopPropagation();
    navigator.clipboard.writeText(text);
    setCopiedId(id);
    setTimeout(() => setCopiedId(null), 2000);
  };

  const sortedOrders = useMemo(() => {
    const sorted = [...orders].sort((a, b) => {
      let comparison = 0;

      switch (sortField) {
        case 'order_number':
          comparison = a.order_number.localeCompare(b.order_number);
          break;
        case 'order_date':
          comparison = new Date(a.order_date).getTime() - new Date(b.order_date).getTime();
          break;
        case 'total_price':
          comparison = a.total_price - b.total_price;
          break;
        case 'financial_status':
          comparison = (a.financial_status || '').localeCompare(b.financial_status || '');
          break;
        case 'fulfillment_status':
          comparison = (a.fulfillment_status || '').localeCompare(b.fulfillment_status || '');
          break;
        case 'customer_name':
          comparison = (a.customer_name || '').localeCompare(b.customer_name || '');
          break;
        default:
          comparison = 0;
      }

      return sortDirection === 'asc' ? comparison : -comparison;
    });

    return sorted;
  }, [orders, sortField, sortDirection]);

  const handleSort = (field: SortField) => {
    if (sortField === field) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDirection('desc');
    }
  };

  const toggleSelectAll = () => {
    if (selectedOrders.size === orders.length) {
      setSelectedOrders(new Set());
    } else {
      setSelectedOrders(new Set(orders.map(o => o.id)));
    }
  };

  const selectPaidUnfulfilled = () => {
    const matching = orders.filter(o =>
      isPaidOrPartiallyRefunded(o.financial_status) &&
      isOrderAwaitingFulfillment(o)
    );
    setSelectedOrders(new Set(matching.map(o => o.id)));
  };

  const toggleSelectOrder = (orderId: string, e: React.MouseEvent) => {
    e.stopPropagation();
    const newSelected = new Set(selectedOrders);
    if (newSelected.has(orderId)) {
      newSelected.delete(orderId);
    } else {
      newSelected.add(orderId);
    }
    setSelectedOrders(newSelected);
  };

  const isPaidOrPartiallyRefunded = (status: string) =>
    status === 'PAID' || status === 'PARTIALLY_REFUNDED';

  const canToggleReady = (order: ShopifyOrder) => {
    return isPaidOrPartiallyRefunded(order.financial_status) &&
      isOrderAwaitingFulfillment(order);
  };

  const canFulfill = (order: ShopifyOrder) => {
    return isPaidOrPartiallyRefunded(order.financial_status) &&
      isOrderAwaitingFulfillment(order);
  };

  const canFinish = (order: ShopifyOrder) => {
    if (!order.is_ready) return false;
    if (!isPaidOrPartiallyRefunded(order.financial_status)) return false;
    if (order.fulfillment_status === 'FULFILLED' || order.fulfilled_at) {
      return false;
    }
    return true;
  };
  
  const isFinished = (order: ShopifyOrder) => {
    return order.fulfillment_status === 'FULFILLED' || !!order.fulfilled_at;
  };

  const canReprint = (order: ShopifyOrder) => {
    return order.fulfillment_status === 'FULFILLED' &&
      (order.label_id || order.tracking_code);
  };

  const selectedOrdersList = useMemo(() =>
    orders.filter(o => selectedOrders.has(o.id)),
  [orders, selectedOrders]);

  const bulkReadyEligible = useMemo(() =>
    selectedOrdersList.filter(o =>
      isPaidOrPartiallyRefunded(o.financial_status) &&
      isOrderAwaitingFulfillment(o) &&
      !o.is_ready
    ).length,
  [selectedOrdersList]);

  const bulkFinishEligible = useMemo(() =>
    selectedOrdersList.filter(o =>
      isPaidOrPartiallyRefunded(o.financial_status) &&
      isOrderAwaitingFulfillment(o) &&
      o.shipping_address?.address1 &&
      o.shipping_address?.city &&
      o.shipping_address?.zip
    ).length,
  [selectedOrdersList]);

  const getStatusBadgeClass = (color: string) => {
    const colorMap: Record<string, string> = {
      green: 'bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-300 border-green-200 dark:border-green-800',
      yellow: 'bg-yellow-100 dark:bg-yellow-900/30 text-yellow-700 dark:text-yellow-300 border-yellow-200 dark:border-yellow-800',
      red: 'bg-red-100 dark:bg-red-900/30 text-red-700 dark:text-red-300 border-red-200 dark:border-red-800',
      blue: 'bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300 border-blue-200 dark:border-blue-800',
      orange: 'bg-orange-100 dark:bg-orange-900/30 text-orange-700 dark:text-orange-300 border-orange-200 dark:border-orange-800',
      gray: 'bg-gray-100 dark:bg-gray-900/30 text-gray-700 dark:text-gray-300 border-gray-200 dark:border-gray-800',
    };
    return colorMap[color] || colorMap.gray;
  };

  const SortableHeader: React.FC<{
    field: SortField;
    children: React.ReactNode;
    className?: string;
    minWidth?: string;
  }> = ({ field, children, className = '', minWidth }) => (
    <th
      className={`px-3 py-3 text-left text-xs font-semibold text-gray-600 dark:text-gray-300 uppercase tracking-wider cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-700/50 transition-colors select-none whitespace-nowrap ${className}`}
      onClick={() => handleSort(field)}
      style={{ minWidth }}
    >
      <div className="flex items-center gap-1.5">
        {children}
        {sortField === field ? (
          sortDirection === 'asc' ? (
            <ChevronUp className="h-4 w-4 text-cyan-600" />
          ) : (
            <ChevronDown className="h-4 w-4 text-cyan-600" />
          )
        ) : (
          <ArrowUpDown className="h-3 w-3 opacity-40" />
        )}
      </div>
    </th>
  );

  if (orders.length === 0) {
    return (
      <div className="card p-12 text-center">
        <Package className="h-16 w-16 text-gray-400 dark:text-gray-600 mx-auto mb-4" />
        <h3 className="text-xl font-semibold text-gray-900 dark:text-gray-100 mb-2">
          No Orders Found
        </h3>
        <p className="text-gray-600 dark:text-gray-400 mb-6">
          No orders match your current filters. Try adjusting your search criteria.
        </p>
      </div>
    );
  }

  return (
    <div className="card overflow-hidden">
      {selectedOrders.size > 0 && (
        <div className="px-4 py-3 bg-cyan-50 dark:bg-cyan-900/20 border-b border-cyan-200 dark:border-cyan-800 flex items-center justify-between">
          <span className="text-sm font-medium text-cyan-700 dark:text-cyan-300">
            {selectedOrders.size} order{selectedOrders.size !== 1 ? 's' : ''} selected
          </span>
          <div className="flex items-center gap-2">
            {onBulkPrintPick && (
              <button
                onClick={() => {
                  onBulkPrintPick(Array.from(selectedOrders));
                }}
                className="inline-flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium rounded-lg bg-cyan-600 text-white hover:bg-cyan-700 transition-colors"
              >
                <ClipboardList className="h-4 w-4" />
                Print Pick
              </button>
            )}
            {onBulkMarkReady && (
              <button
                onClick={() => {
                  onBulkMarkReady(Array.from(selectedOrders));
                }}
                disabled={bulkReadyEligible === 0}
                className="inline-flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium rounded-lg bg-green-600 text-white hover:bg-green-700 transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
              >
                <CheckCircle className="h-4 w-4" />
                Mark Ready ({bulkReadyEligible}/{selectedOrders.size})
              </button>
            )}
            {onBulkFinish && (
              <button
                onClick={() => {
                  onBulkFinish(Array.from(selectedOrders));
                }}
                disabled={bulkFinishEligible === 0}
                className="inline-flex items-center gap-1.5 px-3 py-1.5 text-sm font-semibold rounded-lg bg-gradient-to-r from-green-600 to-emerald-600 text-white hover:from-green-700 hover:to-emerald-700 shadow-sm transition-all disabled:opacity-40 disabled:cursor-not-allowed"
              >
                <PackageCheck className="h-4 w-4" />
                Finish ({bulkFinishEligible}/{selectedOrders.size})
              </button>
            )}
            <button
              onClick={() => setSelectedOrders(new Set())}
              className="text-sm text-cyan-600 dark:text-cyan-400 hover:text-cyan-700 font-medium"
            >
              Clear selection
            </button>
          </div>
        </div>
      )}

      <div className="overflow-x-auto">
        <table className="w-full" style={{ minWidth: '1400px' }}>
          <thead className="bg-gray-50 dark:bg-gray-800/80 border-b border-gray-200 dark:border-gray-700 sticky top-0 z-10">
            <tr>
              <th className="w-20 px-3 py-3">
                <div className="flex items-center gap-2">
                  <input
                    type="checkbox"
                    checked={selectedOrders.size === orders.length && orders.length > 0}
                    onChange={toggleSelectAll}
                    className="w-4 h-4 rounded border-gray-300 dark:border-gray-600 text-cyan-600 focus:ring-cyan-500"
                  />
                  <button
                    onClick={selectPaidUnfulfilled}
                    title="Select Paid & Unfulfilled"
                    className="flex items-center justify-center w-6 h-6 rounded-md text-green-600 dark:text-green-400 hover:bg-green-100 dark:hover:bg-green-900/30 transition-colors"
                  >
                    <ListChecks className="h-4 w-4" />
                  </button>
                </div>
              </th>
              <SortableHeader field="order_number" minWidth="140px">
                <Hash className="h-3.5 w-3.5" />
                Order
              </SortableHeader>
              <SortableHeader field="customer_name" minWidth="240px">
                <User className="h-3.5 w-3.5" />
                Customer
              </SortableHeader>
              <th className="px-3 py-3 text-left text-xs font-semibold text-gray-600 dark:text-gray-300 uppercase tracking-wider whitespace-nowrap" style={{ minWidth: '200px' }}>
                <div className="flex items-center gap-1.5">
                  <ShoppingBag className="h-3.5 w-3.5" />
                  Items
                </div>
              </th>
              <SortableHeader field="order_date" minWidth="130px">
                <Calendar className="h-3.5 w-3.5" />
                Date
              </SortableHeader>
              <SortableHeader field="total_price" minWidth="110px">
                <DollarSign className="h-3.5 w-3.5" />
                Total
              </SortableHeader>
              <SortableHeader field="financial_status" minWidth="120px">
                Payment
              </SortableHeader>
              <SortableHeader field="fulfillment_status" minWidth="130px">
                Fulfillment
              </SortableHeader>
              <th className="px-3 py-3 text-center text-xs font-semibold text-gray-600 dark:text-gray-300 uppercase tracking-wider whitespace-nowrap" style={{ minWidth: '100px' }}>
                Ready
              </th>
              <th className="px-3 py-3 text-left text-xs font-semibold text-gray-600 dark:text-gray-300 uppercase tracking-wider whitespace-nowrap" style={{ minWidth: '180px' }}>
                <div className="flex items-center gap-1.5">
                  <Truck className="h-3.5 w-3.5" />
                  Tracking
                </div>
              </th>
              <th className="px-3 py-3 text-left text-xs font-semibold text-gray-600 dark:text-gray-300 uppercase tracking-wider whitespace-nowrap" style={{ minWidth: '220px' }}>
                Actions
              </th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100 dark:divide-gray-800">
            {sortedOrders.map((order) => {
              const orderAge = getOrderAge(order.order_date);
              const isUnfulfilled = isOrderAwaitingFulfillment(order);
              const showUrgency = isUnfulfilled && order.financial_status === 'PAID';

              const showPartialFulfillment = order.fulfillment_status === 'PARTIALLY_FULFILLED';

              return (
                <tr
                  key={order.id}
                  className={`hover:bg-gray-50 dark:hover:bg-gray-800/50 transition-colors cursor-pointer group ${
                    selectedOrders.has(order.id) ? 'bg-cyan-50/50 dark:bg-cyan-900/10' : ''
                  } ${showPartialFulfillment ? 'bg-yellow-50/60 dark:bg-yellow-900/10 border-l-4 border-yellow-400' : ''}`}
                  onClick={() => onOrderClick(order)}
                >
                  <td className="px-3 py-3">
                    <input
                      type="checkbox"
                      checked={selectedOrders.has(order.id)}
                      onChange={(e) => toggleSelectOrder(order.id, e as unknown as React.MouseEvent)}
                      onClick={(e) => e.stopPropagation()}
                      className="w-4 h-4 rounded border-gray-300 dark:border-gray-600 text-cyan-600 focus:ring-cyan-500"
                    />
                  </td>

                  <td className="px-3 py-3">
                    <div className="flex items-center gap-2.5">
                      <div className={`w-9 h-9 rounded-lg flex items-center justify-center text-white text-xs font-bold shadow-sm flex-shrink-0 ${
                        showUrgency && orderAge.urgent
                          ? 'bg-gradient-to-br from-red-500 to-rose-600'
                          : showUrgency && orderAge.warning
                            ? 'bg-gradient-to-br from-amber-500 to-orange-600'
                            : 'bg-gradient-to-br from-cyan-500 to-blue-600'
                      }`}>
                        {getCustomerInitials(order)}
                      </div>
                      <div className="min-w-0">
                        <div className="flex items-center gap-2">
                          <span className="text-sm font-semibold text-gray-900 dark:text-gray-100">
                            {order.order_number}
                          </span>
                          <button
                            onClick={(e) => copyToClipboard(order.order_number, `order-${order.id}`, e)}
                            className="opacity-0 group-hover:opacity-100 p-1 hover:bg-gray-200 dark:hover:bg-gray-700 rounded transition-all"
                            title="Copy order number"
                          >
                            {copiedId === `order-${order.id}` ? (
                              <Check className="h-3 w-3 text-green-600" />
                            ) : (
                              <Copy className="h-3 w-3 text-gray-400" />
                            )}
                          </button>
                        </div>
                        <div className="flex items-center gap-2 mt-0.5">
                          <span className="text-xs text-gray-500 dark:text-gray-400">
                            {order.line_items.length} {order.line_items.length === 1 ? 'item' : 'items'}
                          </span>
                          {showUrgency && orderAge.days >= 2 && (
                            <span className={`inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[10px] font-medium ${
                              orderAge.urgent
                                ? 'bg-red-100 dark:bg-red-900/30 text-red-700 dark:text-red-400'
                                : 'bg-amber-100 dark:bg-amber-900/30 text-amber-700 dark:text-amber-400'
                            }`}>
                              <AlertCircle className="h-2.5 w-2.5" />
                              {orderAge.days}d old
                            </span>
                          )}
                        </div>
                      </div>
                    </div>
                  </td>

                  <td className="px-3 py-3">
                    <div className="min-w-0">
                      {order.customer_name ? (
                        <>
                          <div className="text-sm font-medium text-gray-900 dark:text-gray-100">
                            {order.customer_name}
                          </div>
                          {order.customer_email && (
                            <div className="flex items-center gap-1.5 mt-0.5">
                              <Mail className="h-3 w-3 text-gray-400 flex-shrink-0" />
                              <span className="text-xs text-gray-500 dark:text-gray-400 truncate max-w-[180px]">
                                {order.customer_email}
                              </span>
                            </div>
                          )}
                          {formatFullAddress(order) && (
                            <div className="flex items-center gap-1.5 mt-0.5">
                              <Globe className="h-3 w-3 text-gray-400 flex-shrink-0" />
                              <span className="text-xs text-gray-500 dark:text-gray-400 truncate max-w-[180px]">
                                {formatFullAddress(order)}
                              </span>
                            </div>
                          )}
                        </>
                      ) : (
                        <span className="text-sm text-gray-400 dark:text-gray-500 italic">No customer info</span>
                      )}
                    </div>
                  </td>

                  <td className="px-3 py-3">
                    <div className="space-y-1">
                      {order.line_items.slice(0, 2).map((item, idx) => (
                        <div key={item.id || idx} className="flex items-center gap-2">
                          <div className="w-6 h-6 bg-gray-100 dark:bg-gray-700 rounded flex items-center justify-center flex-shrink-0">
                            <Package className="h-3 w-3 text-gray-400" />
                          </div>
                          <span className="text-xs text-gray-700 dark:text-gray-300 truncate max-w-[140px]" title={item.title}>
                            {item.quantity}x {item.title}
                          </span>
                        </div>
                      ))}
                      {order.line_items.length > 2 && (
                        <span className="text-xs text-gray-500 dark:text-gray-400 pl-8">
                          +{order.line_items.length - 2} more
                        </span>
                      )}
                    </div>
                  </td>

                  <td className="px-3 py-3">
                    <div className="text-sm font-medium text-gray-900 dark:text-gray-100">
                      {getRelativeTime(order.order_date)}
                    </div>
                    <div className="text-xs text-gray-500 dark:text-gray-400">
                      {new Date(order.order_date).toLocaleDateString()}
                    </div>
                  </td>

                  <td className="px-3 py-3">
                    <div className="text-sm font-bold text-gray-900 dark:text-gray-100">
                      {formatCurrency(order.total_price, order.currency)}
                    </div>
                  </td>

                  <td className="px-3 py-3">
                    <span
                      className={`inline-flex items-center px-2 py-1 text-xs font-semibold rounded-full border ${getStatusBadgeClass(
                        getFinancialStatusColor(order.financial_status)
                      )}`}
                    >
                      {order.financial_status.replace(/_/g, ' ')}
                    </span>
                  </td>

                  <td className="px-3 py-3">
                    <span
                      className={`inline-flex items-center px-2 py-1 text-xs font-semibold rounded-full border ${getStatusBadgeClass(
                        getFulfillmentDisplayColor(order.financial_status, order.fulfillment_status, order.cancelled_at)
                      )}`}
                    >
                      {getFulfillmentDisplayLabel(order.financial_status, order.fulfillment_status, order.cancelled_at)}
                    </span>
                  </td>

                  <td className="px-3 py-3 text-center">
                    {canToggleReady(order) ? (
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          onToggleReady?.(order.id, order.is_ready);
                        }}
                        disabled={togglingReadyId === order.id}
                        className={`inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-medium transition-all ${
                          order.is_ready
                            ? 'bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-300 border border-green-200 dark:border-green-800 hover:bg-green-200 dark:hover:bg-green-900/50'
                            : 'bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-400 border border-gray-200 dark:border-gray-600 hover:bg-gray-200 dark:hover:bg-gray-600'
                        } disabled:opacity-50`}
                        title={order.is_ready ? 'Click to mark as not ready' : 'Click to mark as ready'}
                      >
                        {togglingReadyId === order.id ? (
                          <Loader2 className="h-3 w-3 animate-spin" />
                        ) : order.is_ready ? (
                          <CheckCircle className="h-3 w-3" />
                        ) : (
                          <Circle className="h-3 w-3" />
                        )}
                        {order.is_ready ? 'Yes' : 'No'}
                      </button>
                    ) : (
                      <span className="text-xs text-gray-400 dark:text-gray-500">
                        {order.is_ready ? (
                          <span className="inline-flex items-center gap-1 text-green-600 dark:text-green-400">
                            <CheckCircle className="h-3 w-3" />
                            Yes
                          </span>
                        ) : (
                          '-'
                        )}
                      </span>
                    )}
                  </td>

                  <td className="px-3 py-3">
                    {order.tracking_code ? (
                      <div className="flex items-center gap-2">
                        <div className="w-7 h-7 rounded-lg bg-green-100 dark:bg-green-900/30 flex items-center justify-center flex-shrink-0">
                          <Truck className="h-3.5 w-3.5 text-green-600 dark:text-green-400" />
                        </div>
                        <div className="min-w-0 flex-1">
                          <div className="text-xs font-medium text-gray-900 dark:text-gray-100">
                            {order.carrier || 'Unknown'}
                          </div>
                          <div className="flex items-center gap-1">
                            {getTrackingUrl(order.carrier, order.tracking_code) ? (
                              <a
                                href={getTrackingUrl(order.carrier, order.tracking_code)!}
                                target="_blank"
                                rel="noopener noreferrer"
                                onClick={(e) => e.stopPropagation()}
                                className="text-xs text-cyan-600 dark:text-cyan-400 hover:underline font-mono truncate max-w-[100px]"
                                title={order.tracking_code}
                              >
                                {order.tracking_code}
                              </a>
                            ) : (
                              <span className="text-xs text-gray-500 dark:text-gray-400 font-mono truncate max-w-[100px]" title={order.tracking_code}>
                                {order.tracking_code}
                              </span>
                            )}
                            <button
                              onClick={(e) => copyToClipboard(order.tracking_code!, `track-${order.id}`, e)}
                              className="p-0.5 hover:bg-gray-200 dark:hover:bg-gray-700 rounded transition-all flex-shrink-0"
                              title="Copy tracking"
                            >
                              {copiedId === `track-${order.id}` ? (
                                <Check className="h-3 w-3 text-green-600" />
                              ) : (
                                <Copy className="h-3 w-3 text-gray-400" />
                              )}
                            </button>
                            {getTrackingUrl(order.carrier, order.tracking_code) && (
                              <a
                                href={getTrackingUrl(order.carrier, order.tracking_code)!}
                                target="_blank"
                                rel="noopener noreferrer"
                                onClick={(e) => e.stopPropagation()}
                                className="p-0.5 hover:bg-gray-200 dark:hover:bg-gray-700 rounded transition-all flex-shrink-0"
                                title="Track package"
                              >
                                <ExternalLink className="h-3 w-3 text-gray-400" />
                              </a>
                            )}
                          </div>
                        </div>
                      </div>
                    ) : (
                      <span className="text-xs text-gray-400 dark:text-gray-500">
                        {order.fulfillment_status === 'FULFILLED' ? 'No tracking' : '-'}
                      </span>
                    )}
                  </td>

                  <td className="px-3 py-3">
                    <div className="flex items-center gap-1.5">
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          onOrderClick(order);
                        }}
                        className="inline-flex items-center gap-1 px-2.5 py-1.5 text-xs font-medium rounded-lg bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-600 transition-colors"
                      >
                        <Eye className="h-3.5 w-3.5" />
                        View
                      </button>
                      {isFinished(order) ? (
                        <span className="inline-flex items-center gap-1 px-3 py-1.5 text-xs font-semibold rounded-lg bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-400 border border-gray-200 dark:border-gray-600">
                          <CheckCircle className="h-3.5 w-3.5" />
                          Finished
                        </span>
                      ) : canFinish(order) ? (
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            onFinishClick?.(order);
                          }}
                          disabled={finishingOrderId === order.shopify_order_id}
                          className="inline-flex items-center gap-1 px-3 py-1.5 text-xs font-semibold rounded-lg bg-gradient-to-r from-green-600 to-emerald-600 text-white hover:from-green-700 hover:to-emerald-700 shadow-sm hover:shadow-md transition-all disabled:opacity-50"
                          title="Generate PostNL label and fulfill order in Shopify"
                        >
                          {finishingOrderId === order.shopify_order_id ? (
                            <Loader2 className="h-3.5 w-3.5 animate-spin" />
                          ) : (
                            <PackageCheck className="h-3.5 w-3.5" />
                          )}
                          Finish
                        </button>
                      ) : null}
                      {canReprint(order) && (
                        <>
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              onReprintClick?.(order);
                            }}
                            disabled={reprintingOrderId === order.shopify_order_id}
                            className="inline-flex items-center gap-1 px-2.5 py-1.5 text-xs font-medium rounded-lg bg-blue-50 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300 border border-blue-200 dark:border-blue-800 hover:bg-blue-100 dark:hover:bg-blue-900/50 transition-colors disabled:opacity-50"
                            title="Reprint shipping label"
                          >
                            {reprintingOrderId === order.shopify_order_id ? (
                              <Loader2 className="h-3.5 w-3.5 animate-spin" />
                            ) : (
                              <Printer className="h-3.5 w-3.5" />
                            )}
                          </button>
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              onPrintInvoiceClick?.(order);
                            }}
                            className="inline-flex items-center gap-1 px-2.5 py-1.5 text-xs font-medium rounded-lg bg-cyan-50 dark:bg-cyan-900/30 text-cyan-700 dark:text-cyan-300 border border-cyan-200 dark:border-cyan-800 hover:bg-cyan-100 dark:hover:bg-cyan-900/50 transition-colors"
                            title="Print invoice"
                          >
                            <FileText className="h-3.5 w-3.5" />
                          </button>
                        </>
                      )}
                      {canFulfill(order) && !canFinish(order) && (
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            onFulfillClick?.(order);
                          }}
                          className="inline-flex items-center gap-1 px-2.5 py-1.5 text-xs font-medium rounded-lg bg-amber-50 dark:bg-amber-900/30 text-amber-700 dark:text-amber-300 border border-amber-200 dark:border-amber-800 hover:bg-amber-100 dark:hover:bg-amber-900/50 transition-colors"
                          title="Manual fulfillment - mark as Ready first for one-click Finish"
                        >
                          <Truck className="h-3.5 w-3.5" />
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      <div className="lg:hidden divide-y divide-gray-200 dark:divide-gray-700">
        {sortedOrders.map((order) => {
          const orderAge = getOrderAge(order.order_date);
          const isUnfulfilled = isOrderAwaitingFulfillment(order);
          const showUrgency = isUnfulfilled && order.financial_status === 'PAID';

          return (
            <div
              key={order.id}
              className="p-4 hover:bg-gray-50 dark:hover:bg-gray-800/50 transition-colors"
              onClick={() => onOrderClick(order)}
            >
              <div className="flex items-start gap-3 mb-3">
                <div className={`w-10 h-10 rounded-lg flex items-center justify-center text-white text-xs font-bold shadow-sm flex-shrink-0 ${
                  showUrgency && orderAge.urgent
                    ? 'bg-gradient-to-br from-red-500 to-rose-600'
                    : showUrgency && orderAge.warning
                      ? 'bg-gradient-to-br from-amber-500 to-orange-600'
                      : 'bg-gradient-to-br from-cyan-500 to-blue-600'
                }`}>
                  {getCustomerInitials(order)}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-start justify-between">
                    <div className="min-w-0 flex-1">
                      <div className="flex items-center gap-2">
                        <span className="text-sm font-semibold text-gray-900 dark:text-gray-100">
                          {order.order_number}
                        </span>
                        {showUrgency && orderAge.days >= 2 && (
                          <span className={`inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[10px] font-medium ${
                            orderAge.urgent
                              ? 'bg-red-100 dark:bg-red-900/30 text-red-700 dark:text-red-400'
                              : 'bg-amber-100 dark:bg-amber-900/30 text-amber-700 dark:text-amber-400'
                          }`}>
                            {orderAge.days}d
                          </span>
                        )}
                      </div>
                      {order.customer_name && (
                        <div className="text-sm text-gray-700 dark:text-gray-300 truncate">
                          {order.customer_name}
                        </div>
                      )}
                      {formatFullAddress(order) && (
                        <div className="text-xs text-gray-500 dark:text-gray-400 flex items-center gap-1 mt-0.5">
                          <Globe className="h-3 w-3 flex-shrink-0" />
                          <span className="truncate">{formatFullAddress(order)}</span>
                        </div>
                      )}
                    </div>
                    <div className="text-right ml-2 flex-shrink-0">
                      <div className="text-sm font-bold text-gray-900 dark:text-gray-100">
                        {formatCurrency(order.total_price, order.currency)}
                      </div>
                      <div className="text-xs text-gray-500 dark:text-gray-400">
                        {order.line_items.length} items
                      </div>
                    </div>
                  </div>
                  <div className="text-xs text-gray-500 dark:text-gray-400 flex items-center gap-1 mt-1">
                    <Clock className="h-3 w-3" />
                    {getRelativeTime(order.order_date)}
                  </div>
                </div>
              </div>

              <div className="flex flex-wrap items-center gap-2 mb-3">
                <span
                  className={`px-2 py-0.5 text-xs font-semibold rounded-full border ${getStatusBadgeClass(
                    getFinancialStatusColor(order.financial_status)
                  )}`}
                >
                  {order.financial_status.replace(/_/g, ' ')}
                </span>
                <span
                  className={`px-2 py-0.5 text-xs font-semibold rounded-full border ${getStatusBadgeClass(
                    getFulfillmentDisplayColor(order.financial_status, order.fulfillment_status, order.cancelled_at)
                  )}`}
                >
                  {getFulfillmentDisplayLabel(order.financial_status, order.fulfillment_status, order.cancelled_at)}
                </span>
                {order.is_ready && (
                  <span className="inline-flex items-center gap-1 px-2 py-0.5 text-xs font-semibold rounded-full bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-300 border border-green-200 dark:border-green-800">
                    <CheckCircle className="h-3 w-3" />
                    Ready
                  </span>
                )}
              </div>

              {order.tracking_code && (
                <div className="flex items-center gap-2 mb-3 p-2 bg-green-50 dark:bg-green-900/20 rounded-lg">
                  <Truck className="h-4 w-4 text-green-600 dark:text-green-400" />
                  <div className="flex-1 min-w-0">
                    <div className="text-xs font-medium text-green-700 dark:text-green-300">
                      {order.carrier || 'Shipped'}
                    </div>
                    <div className="text-xs text-green-600 dark:text-green-400 font-mono truncate">
                      {order.tracking_code}
                    </div>
                  </div>
                  {getTrackingUrl(order.carrier, order.tracking_code) && (
                    <a
                      href={getTrackingUrl(order.carrier, order.tracking_code)!}
                      target="_blank"
                      rel="noopener noreferrer"
                      onClick={(e) => e.stopPropagation()}
                      className="p-1.5 bg-green-100 dark:bg-green-800 rounded-lg"
                    >
                      <ExternalLink className="h-4 w-4 text-green-600 dark:text-green-400" />
                    </a>
                  )}
                </div>
              )}

              <div className="flex items-center gap-2 flex-wrap">
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    onOrderClick(order);
                  }}
                  className="inline-flex items-center justify-center gap-1.5 px-3 py-2 rounded-lg text-xs font-medium bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300"
                >
                  <Eye className="h-3.5 w-3.5" />
                  View
                </button>
                {canToggleReady(order) && (
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      onToggleReady?.(order.id, order.is_ready);
                    }}
                    disabled={togglingReadyId === order.id}
                    className={`inline-flex items-center justify-center gap-1.5 px-3 py-2 rounded-lg text-xs font-medium transition-all ${
                      order.is_ready
                        ? 'bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-300 border border-green-200 dark:border-green-800'
                        : 'bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-400 border border-gray-200 dark:border-gray-600'
                    } disabled:opacity-50`}
                  >
                    {togglingReadyId === order.id ? (
                      <Loader2 className="h-3.5 w-3.5 animate-spin" />
                    ) : order.is_ready ? (
                      <CheckCircle className="h-3.5 w-3.5" />
                    ) : (
                      <Circle className="h-3.5 w-3.5" />
                    )}
                    {order.is_ready ? 'Ready' : 'Mark Ready'}
                  </button>
                )}
                {isFinished(order) ? (
                  <span className="inline-flex items-center justify-center gap-1.5 px-4 py-2 rounded-lg text-xs font-semibold bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-400 border border-gray-200 dark:border-gray-600">
                    <CheckCircle className="h-4 w-4" />
                    Finished
                  </span>
                ) : canFinish(order) ? (
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      onFinishClick?.(order);
                    }}
                    disabled={finishingOrderId === order.shopify_order_id}
                    className="inline-flex items-center justify-center gap-1.5 px-4 py-2 rounded-lg text-xs font-semibold bg-gradient-to-r from-green-600 to-emerald-600 text-white shadow-md disabled:opacity-50"
                  >
                    {finishingOrderId === order.shopify_order_id ? (
                      <Loader2 className="h-4 w-4 animate-spin" />
                    ) : (
                      <PackageCheck className="h-4 w-4" />
                    )}
                    Finish Order
                  </button>
                ) : null}
                {canReprint(order) && (
                  <>
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        onReprintClick?.(order);
                      }}
                      disabled={reprintingOrderId === order.shopify_order_id}
                      className="inline-flex items-center justify-center gap-1.5 px-3 py-2 rounded-lg text-xs font-medium bg-blue-50 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300 border border-blue-200 dark:border-blue-800 disabled:opacity-50"
                    >
                      {reprintingOrderId === order.shopify_order_id ? (
                        <Loader2 className="h-3.5 w-3.5 animate-spin" />
                      ) : (
                        <Printer className="h-3.5 w-3.5" />
                      )}
                      Reprint
                    </button>
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        onPrintInvoiceClick?.(order);
                      }}
                      className="inline-flex items-center justify-center gap-1.5 px-3 py-2 rounded-lg text-xs font-medium bg-cyan-50 dark:bg-cyan-900/30 text-cyan-700 dark:text-cyan-300 border border-cyan-200 dark:border-cyan-800"
                    >
                      <FileText className="h-3.5 w-3.5" />
                      Invoice
                    </button>
                  </>
                )}
                {canFulfill(order) && !canFinish(order) && (
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      onFulfillClick?.(order);
                    }}
                    className="inline-flex items-center justify-center gap-1.5 px-3 py-2 rounded-lg text-xs font-medium bg-amber-50 dark:bg-amber-900/30 text-amber-700 dark:text-amber-300 border border-amber-200 dark:border-amber-800"
                  >
                    <Truck className="h-3.5 w-3.5" />
                    Fulfill
                  </button>
                )}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
});

OrdersList.displayName = 'OrdersList';

export default OrdersList;
